import { toast } from 'sonner'
import { ApiClientError } from '@/lib/api/client'
import { REFUSAL_KEY_BY_SHARED_CODE, REFUSAL_KEY_BY_STATUS, logRefusal } from '@/lib/api/refusal-key'
import { ErApiError } from '@/lib/er/api'
import { ER_TRIAGE_REFUSAL_KEYS } from '@/lib/er/triage-refusal'
import type { SnrIssue } from '@/lib/er/snr-query/types'

/**
 * What Search & Remediate tells an analyst when the API refuses something, as catalogue keys
 * (CLAUDE.md rule 9: never the API's English `message`, never a bare code). Every refusal carries
 * an `errorCode`; the compile result carries the same codes as `blockers` and `warnings`
 * (`SnrIssue.code`). Each code reads `er.snr.errors.<code>`.
 *
 * The first block is the query core's (apps/api/src/modules/er/snr-query/codes.ts); the second
 * is the contract's pipeline refusals (contracts/snr-api.md). A code this list does not know
 * reads the HTTP status's sentence, else the action's own "could not" sentence, so a new server
 * code is never shown raw. `snr-errors-have-words.guard.spec.ts` proves each code has a sentence
 * in all four languages.
 */
export const SNR_QUERY_ERROR_CODES = [
  'er_snr_query_invalid',
  'er_snr_query_unknown_field',
  'er_snr_query_op_not_allowed',
  'er_snr_query_value_invalid',
  'er_snr_query_too_deep',
  'er_snr_query_too_many_conditions',
  'er_snr_query_too_many_children',
  'er_snr_query_too_many_values',
  'er_snr_query_header_invalid',
  // The compiler's blockers and warnings.
  'er_snr_query_unbounded',
  'er_snr_query_window_empty',
  'er_snr_query_no_providers',
  'er_snr_query_wide_date_only',
  'er_snr_query_broad_only',
  'er_snr_query_reads_bodies',
  'er_snr_query_downloads_attachments',
  'er_snr_query_text_checked_locally',
  'er_snr_query_time_zone_fallback',
] as const

export const SNR_PIPELINE_ERROR_CODES = [
  'er_snr_query_blocked',
  'er_snr_plan_stale',
  'er_snr_domain_forbidden',
  'er_snr_mailbox_forbidden',
  'er_snr_group_forbidden',
  'er_snr_scope_empty',
  'er_snr_scope_invalid',
  'er_snr_compile_body_invalid',
  'er_snr_domain_not_accessible',
  'er_snr_no_domains',
  'er_snr_run_not_found',
  'er_snr_run_state_invalid',
  'er_snr_run_not_v2',
  'er_snr_run_not_finished',
  'er_snr_run_is_dry_run',
  'er_snr_selection_foreign',
  'er_snr_job_still_running',
  'er_snr_where_invalid',
  'er_snr_count_required',
  'er_snr_count_mismatch',
  'er_snr_confirm_expired',
  'er_snr_confirm_invalid',
  'er_snr_cap_exceeded',
  'er_snr_action_not_offered',
  'er_snr_folder_name_required',
  'er_snr_selection_empty',
  'er_snr_job_not_found',
  'er_snr_nothing_to_undo',
  'er_snr_nothing_to_retry',
  'er_snr_sort_invalid',
  'er_snr_where_unsupported',
  'er_snr_saved_not_found',
  'er_snr_saved_not_owner',
  'er_snr_recipients_foreign',
  // Warnings of a preview.
  'er_snr_coverage_incomplete',
  'er_snr_many_message_ids',
  // The review fixes of 27 Oct 2026 (contracts/snr-api.md "Changes", snr-fix).
  'er_snr_confirm_used',
  'er_snr_confirm_required',
  'er_snr_folder_name_reserved',
  'er_snr_job_state_invalid',
  'er_snr_already_undone',
  'er_snr_mailbox_inactive',
  'er_snr_saved_name_required',
  'er_snr_message_not_found',
  'er_snr_headers_unavailable',
  'er_snr_setting_invalid',
  // Trash and Delete stay, behind their permissions (restore-delete-snr, 27 Oct 2026).
  'er_snr_not_undoable',
] as const

/**
 * A refused permanent Delete: the ER-wide 403 `er_delete_permission_missing` with the permission that is missing
 * (the shared Delete gate, er-delete-gate.ts). Not an `er_snr_` code: its sentence depends on `reason`, so it reads
 * `er.snr.deleteGate.<reason>` (snrRefusalKey), never `er.snr.errors.<code>`.
 */
export const SNR_DELETE_REFUSAL_CODE = 'er_delete_permission_missing'
const DELETE_REASON_KEYS: Readonly<Record<string, string>> = {
  role: 'er.snr.deleteGate.role',
  switch: 'er.snr.deleteGate.switch',
  provider_scope: 'er.snr.deleteGate.providerScope',
}

export interface SnrDeleteRefusal {
  reason: 'role' | 'switch' | 'provider_scope' | 'unknown'
  /** The provider split the preview counted (present on a preview's refusal). */
  byProvider: { microsoft: number; google: number } | null
}

/** The refused Delete in a refusal, with what the preview counted; null when the refusal is something else. */
export function snrDeleteRefusalOf(error: unknown): SnrDeleteRefusal | null {
  if (snrErrorCodeOf(error) !== SNR_DELETE_REFUSAL_CODE) return null
  const raw = error instanceof ErApiError ? error.data : error instanceof ApiClientError ? error.body : undefined
  const body = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {}
  const reason = body.reason === 'role' || body.reason === 'switch' || body.reason === 'provider_scope' ? body.reason : 'unknown'
  const split = body.byProvider as { microsoft?: unknown; google?: unknown } | undefined
  const byProvider =
    split && typeof split.microsoft === 'number' && typeof split.google === 'number'
      ? { microsoft: split.microsoft, google: split.google }
      : null
  return { reason, byProvider }
}

/**
 * Ask AI (task 4.7, POST /ai/parse): the three refusals of its own. `er_snr_ai_unparsed` (422) is "no valid filter came
 * back, or there was nothing to search for"; the analyst's filters are untouched. Each reads `er.snr.errors.<code>`.
 */
export const SNR_AI_ERROR_CODES = ['er_snr_ai_sentence_invalid', 'er_snr_ai_unparsed', 'er_snr_ai_unavailable'] as const

export const SNR_ERROR_CODES: readonly string[] = [...SNR_QUERY_ERROR_CODES, ...SNR_PIPELINE_ERROR_CODES, ...SNR_AI_ERROR_CODES]

/** The catalogue key of an S&R errorCode. */
export function snrErrorKey(code: string): string {
  return `er.snr.errors.${code}`
}

/** errorCode -> key, plus the ER-wide codes S&R can hit (no accessible domain, domain required). */
export const SNR_REFUSAL_KEYS: ReadonlyMap<string, string> = new Map<string, string>([
  ...SNR_ERROR_CODES.map((code): [string, string] => [code, snrErrorKey(code)]),
  ...ER_TRIAGE_REFUSAL_KEYS,
])

/** The errorCode of a refusal, whichever shape carries it (erFetch rethrows ApiClientError as ErApiError). */
export function snrErrorCodeOf(error: unknown): string | null {
  if (error instanceof ApiClientError) return error.errorCode
  if (error instanceof ErApiError) {
    const body = error.data
    const code = body && typeof body === 'object' ? (body as { errorCode?: unknown }).errorCode : undefined
    return typeof code === 'string' && code ? code : null
  }
  return null
}

/**
 * How many seconds a throttled provider asked the analyst to wait (a 503's `retryAfterSec`), rounded up to a whole
 * second, or null when the refusal carries none.
 */
export function snrRetryAfterSec(error: unknown): number | null {
  if (!(error instanceof ErApiError)) return null
  const body = error.data
  const raw = body && typeof body === 'object' ? (body as { retryAfterSec?: unknown }).retryAfterSec : undefined
  return typeof raw === 'number' && Number.isFinite(raw) && raw > 0 ? Math.ceil(raw) : null
}

export function snrErrorStatusOf(error: unknown): number | null {
  if (error instanceof ApiClientError) return error.statusCode
  if (error instanceof ErApiError) return error.status
  return null
}

/**
 * The sentence for a refused S&R call: its errorCode's, else a code every screen shares (a write
 * blocked while impersonating), else the HTTP status's (400 / 403 / 404), else the action's own
 * "could not" sentence (a server fault, a lost connection).
 */
export function snrRefusalKey(error: unknown, failedKey: string): string {
  const code = snrErrorCodeOf(error)
  // A refused Delete says WHICH permission is missing (role, switch, provider), never a bare "forbidden".
  if (code === SNR_DELETE_REFUSAL_CODE) {
    const refusal = snrDeleteRefusalOf(error)
    return DELETE_REASON_KEYS[refusal?.reason ?? ''] ?? 'er.snr.deleteGate.unknown'
  }
  const byCode = code ? SNR_REFUSAL_KEYS.get(code) ?? REFUSAL_KEY_BY_SHARED_CODE.get(code) : undefined
  if (byCode) return byCode
  const status = snrErrorStatusOf(error)
  return (status !== null ? REFUSAL_KEY_BY_STATUS[status] : undefined) ?? failedKey
}

/** The key of a compile issue (a blocker or a warning). */
export function snrIssueKey(issue: SnrIssue): string {
  return SNR_REFUSAL_KEYS.get(issue.code) ?? 'er.snr.errors.unknown'
}

/** `reason@path` of a widened clause: the sentence's key and the condition it is about. */
export function snrWidenedParts(entry: string): { key: string; path: string | null } {
  const at = entry.indexOf('@')
  const reason = at < 0 ? entry : entry.slice(0, at)
  const path = at < 0 ? null : entry.slice(at + 1)
  const known =
    reason === 'not_over_inexact' ||
    reason === 'or_inexpressible' ||
    reason === 'sent_drafts_by_evaluator' ||
    reason === 'clause_too_long' ||
    reason === 'fallback_without'
  return { key: known ? `er.snr.widened.${reason}` : 'er.snr.widened.unknown', path: path === '' ? null : path }
}

/**
 * Names a Move may not use, as the server refuses them (er_snr_folder_name_reserved): a system folder or label
 * (Inbox, Junk / Spam, Trash / Deleted Items, Drafts, Sent Items, Outbox). A Move creates and fills a folder of its
 * own; the system folders are not folders it should create, and the verbs that target them have their own buttons
 * (Inbox, Junk). Compared lower-case with single spaces; a Gmail category label (CATEGORY_*) is system too.
 */
const RESERVED_FOLDER_NAMES: ReadonlySet<string> = new Set([
  'inbox',
  'spam',
  'junk',
  'junk email',
  'junk e-mail',
  'trash',
  'bin',
  'deleted',
  'deleted items',
  'sent',
  'sent items',
  'draft',
  'drafts',
  'outbox',
])

/** Is this a folder or label name a Move must not use? The page refuses it before asking the server. */
export function snrReservedFolderName(name: string): boolean {
  const folded = name.trim().replace(/\s+/g, ' ').toLowerCase()
  return RESERVED_FOLDER_NAMES.has(folded) || folded.startsWith('category_')
}

/**
 * The `onError` of an S&R write: the analyst reads snrRefusalKey's sentence, and what the API
 * said goes to the console under `what`, for support.
 */
export function refuseSnr(t: (key: string) => string, what: string, failedKey: string) {
  return (err: unknown) => {
    logRefusal(what, err)
    toast.error(t(snrRefusalKey(err, failedKey)))
  }
}

// ── Ask AI ──────────────────────────────────────────────────────────────────────

/** The two AI credit ceilings (the AI quota guard's 429s): the AI screens already word them. */
const SNR_AI_QUOTA_CODES: ReadonlySet<string> = new Set(['quota_exhausted_use_own_key', 'daily_ai_limit_reached'])

/** How many of the model's reasons one refusal shows, and how long each may be (the text is the model's, not ours). */
const SNR_AI_DETAIL_MAX = 3
const SNR_AI_DETAIL_CHARS = 240

/**
 * The sentence for a refused Ask AI request, and the reasons AI gave when it found nothing to search for.
 *  - its own three codes, and any other S&R code, read `er.snr.errors.<code>`;
 *  - the monthly and the daily AI ceilings read the sentences the AI screens use (`aiErrors.<code>`);
 *  - a 429 with no code is the per-user rate limit;
 *  - anything else reads the HTTP status's sentence, else `er.snr.ai.failed`.
 * `details` are plain strings out of the 422 body (`unsupported`), few and short; the page shows them as typed text.
 */
export function snrAiRefusal(error: unknown): { key: string; details: string[] } {
  const code = snrErrorCodeOf(error)
  const status = snrErrorStatusOf(error)
  let key: string
  if (code && SNR_AI_QUOTA_CODES.has(code)) key = `aiErrors.${code}`
  else if (!code && status === 429) key = 'er.snr.errors.er_snr_ai_rate_limited'
  else key = snrRefusalKey(error, 'er.snr.ai.failed')

  let details: string[] = []
  if (code === 'er_snr_ai_unparsed' && error instanceof ErApiError) {
    const body = error.data && typeof error.data === 'object' ? (error.data as { unsupported?: unknown }).unsupported : undefined
    if (Array.isArray(body)) {
      details = body
        .filter((line): line is string => typeof line === 'string' && line.trim() !== '')
        .slice(0, SNR_AI_DETAIL_MAX)
        .map((line) => (line.length > SNR_AI_DETAIL_CHARS ? `${line.slice(0, SNR_AI_DETAIL_CHARS - 1)}…` : line))
    }
  }
  return { key, details }
}
